import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useAuth } from "../contexts/auth-context";
import { useTranslation } from "react-i18next";

import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { twMerge } from "tailwind-merge";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getRedirectIntent, clearRedirectIntent } from "../utils/redirect-intent";
import {
    ChevronDown,
    ChevronUp,
    Mars,
    MoveRight,
    Pen,
    Venus,
    X,
} from "lucide-react";
import toast from "react-hot-toast";
import {
    authIdentifierNames,
    nationalities,
} from "../constants";
import {
    doc,
    Timestamp,
    getDocs,
    limit,
    orderBy,
    query,
    where,
    getDoc,
} from "firebase/firestore";
import {
    calculateAge,
    splitCamel,
} from "../utils/common";
import {
    FIGHTER_CONSENT_AND_RESPONSIBILITY_AGREEMENT_PATH,
    LANDING_PATH,
    PROFILE_PATH,
} from "../App";
import {
    fighterRequestsCollection,
    registerFighter,
    updateRegisterFighter,
} from "../utils/firebase";
import Preview from "../components/preview";
import FighterRecordsTable from "../components/fighter-records-table";
import LanguageSwitcher from "../components/language-switcher";

export default function FighterRegister() {
    const { id } = useParams();
    const { currentUser, refreshUser, optimisticallyUpdateToFighter } =
        useAuth();
    const { t } = useTranslation();
    const [prevIndex, setPrevIndex] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const fighterRegisterContainer = useRef(null);
    const inputsRef = useRef([]);
    const prevButton = useRef(null);
    const nextButton = useRef(null);
    const fighterPictureInputButton = useRef(null);
    const navigate = useNavigate();
    const redirectIntent = getRedirectIntent();
    const [formData, setFormData] = useState({
        name: currentUser.displayName ?? "",
        nationality: "Malaysian",
        email: currentUser.email ?? "",
        birthdate: null,
        age: "",
        gender: "",
        phoneNumber: currentUser.phoneNumber ?? "",
        emergencyContactNumber: "",
        records: {
            BOXING: { win: "0", loss: "0", draw: "0" },
            MUAY_THAI: { win: "0", loss: "0", draw: "0" },
            KICKBOXING: { win: "0", loss: "0", draw: "0" },
            MMA: { win: "0", loss: "0", draw: "0" },
        },
        icOrPassport: "",
        gymNameOrTeamName: "",
        weightClass: "",
        height: "",
        walkOutMusic: "",
        igUsername: "",
        receipt: null,
        fighterPicture: null,
    });
    const cooldown = useRef(false);
    const cooldownTime = 1000;

    const [animating, setAnimating] = useState(false);

    const [birthdateString, setBirthdateString] = useState("");

    const handleRecordChange = (entry, key, value) => {
        setFormData((prev) => {
            return {
                ...prev,
                records: {
                    ...prev.records,
                    [entry]: {
                        ...prev.records[entry],
                        [key]: (parseInt(value) ?? 0) > 0 ? parseInt(value) : 0,
                    },
                },
            };
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const authIdentifierName = authIdentifierNames[currentUser.authMethod];
        if (name === authIdentifierName) {
            return toast.error(
                `You cannot change the ${splitCamel(authIdentifierName)}.`
            );
        }

        switch (name) {
            case "birthdate":
                setBirthdateString(value);
                if (value) {
                    const date = new Date(value);
                    if (!isNaN(date.getTime())) {
                        const a = calculateAge(date);
                        setFormData((prev) => {
                            return {
                                ...prev,
                                birthdate: Timestamp.fromDate(date),
                                age: a,
                            };
                        });
                    }
                }
                break;
            case "age":
                setFormData((prev) => {
                    const now = new Date();
                    const newYear = now.getFullYear() - value;

                    const d = prev?.birthdate?.toDate() ?? new Date();
                    d.setFullYear(newYear);

                    return {
                        ...prev,
                        age: value,
                        birthdate: Timestamp.fromDate(d),
                    };
                });
                break;
            default:
                setFormData((prev) => {
                    return {
                        ...prev,
                        [name]: value,
                    };
                });
                break;
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) {
            setFormData((prev) => ({
                ...prev,
                [e.target.name]: null,
            }));
            return;
        }

        // Get file extension
        const fileName = file.name;
        const fileExt = fileName.split(".").pop().toLowerCase();

        // List of allowed extensions
        const allowedExts =
            e.target.name === "receipt"
                ? ["pdf", "jpg", "jpeg", "png", "webp"]
                : ["jpg", "jpeg", "png", "webp"];

        if (!allowedExts.includes(fileExt)) {
            toast.error(
                `Please upload a valid file type: ${allowedExts.join(", ")}`
            );
            e.target.value = ""; // Clear the input
            return;
        }

        // Check file size (e.g., 5MB max)
        const maxSize = 25 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            toast.error("File size too large (max 25MB)");
            e.target.value = "";
            return;
        }

        // Process the valid file
        setFormData((prev) => ({
            ...prev,
            [e.target.name]: file,
        }));
    };

    const handleClose = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate(LANDING_PATH);
        }
    };

    const handleSubmit = useCallback(
        (e) => {
            if (!birthdateString) {
                return toast.error(t("fighterRegister.errors.birthdate"));
            }

            const date = new Date(birthdateString);
            if (isNaN(date.getTime())) {
                return toast.error(t("fighterRegister.errors.birthdate"));
            }

            // Validate date is not in the future
            if (date > new Date()) {
                return toast.error(t("fighterRegister.errors.birthdate"));
            }

            // Validate reasonable age range (e.g., between 12 and 120 years)
            const a = calculateAge(date);
            if (a < 12 || a > 120) {
                return toast.error(t("fighterRegister.errors.ageRange"));
            }

            const submitData = {
                ...formData,
                birthdate: Timestamp.fromDate(date),
                age: a,
            };

            if (!!id || !!formData?.id) {
                if (!formData.fighterPicture) {
                    return toast.error(
                        "Fighter Profile or Receipt cannot be empty."
                    );
                }
                toast.promise(
                    updateRegisterFighter(id ?? formData?.id, submitData),
                    {
                        success: () => {
                            navigate(PROFILE_PATH);
                            return "Submit Successful";
                        },
                        error: (error) => {
                            console.error(error);
                            return "Somthing went wrong when update fighter profile, please try it again later";
                        },
                        loading: "Submitting",
                    }
                );
            } else {
                toast.promise(registerFighter(currentUser, submitData), {
                    success: (result) => {
                        if (result?.autoApproved) {
                            // Optimistically update frontend immediately
                            optimisticallyUpdateToFighter(submitData);

                            // Clear redirect intent and navigate to event registration page or homepage
                            clearRedirectIntent();
                            navigate(redirectIntent || LANDING_PATH, {
                                replace: true,
                            });

                            // Sync with backend in background (non-blocking)
                            setTimeout(async () => {
                                await refreshUser();
                            }, 1000);

                            return "Registration Successful! Your fighter profile has been automatically approved.";
                        } else {
                            navigate(PROFILE_PATH);
                            return "Registration Successful! Your request is pending admin approval.";
                        }
                    },
                    error: (error) => {
                        console.error(error);
                        return "Something went wrong when registering fighter, please try again later";
                    },
                    loading: "Submitting",
                });
            }
        },
        [
            currentUser?.uid,
            formData,
            id,
            navigate,
            birthdateString,
        ]
    );

    const handleWheel = (e) => {
        if (cooldown.current) return;

        const power = 500;
        if (e.deltaY <= -power) {
            if (inputs[currentIndex].id === "submit") return;
            prev();
            startCooldown();
        } else if (e.deltaY >= power) {
            next();
            startCooldown();
        }

        function startCooldown() {
            cooldown.current = true;
            setTimeout(() => {
                cooldown.current = false;
            }, cooldownTime);
        }
    };

    const inputs = useMemo(() => {
        const acknowledgement = {
            containerClassName: "",
            title: (
                <span>
                    <b>{t("fighterRegister.acknowledgment.title")}</b>
                </span>
            ),
            desc: (
                <span>
                    {t("fighterRegister.acknowledgment.desc")}{" "}
                    <Link
                        to={FIGHTER_CONSENT_AND_RESPONSIBILITY_AGREEMENT_PATH}
                        target="_self"
                        className="link text-blue-400 hover:text-blue-500"
                    >
                        {t("fighterRegister.acknowledgment.link")}
                    </Link>
                </span>
            ),
            input: null,
            buttonText: t("fighterRegister.acknowledgment.button"),
        };
        /**
         * {
         *  type: 'text' | 'select' | 'date' | 'custom' ,
         *  label: string,
         *  desc: string,
         *  placeholder?: string,
         *  name: string,
         *  id: string,
         *  className?: string,
         *  onChange: Function(event),
         *  options?: { value: string, label: string }[]
         *  children?: any
         * }
         */
        const nameInput = {
            title: (
                <span>
                    {t("fighterRegister.fields.name.title")}{" "}
                    <span className="text-sm text-gray-500">
                        {t("fighterRegister.fields.name.subtitle")}
                    </span>
                </span>
            ),
            desc: t("fighterRegister.fields.name.desc"),
            placeholder: t("fighterRegister.fields.name.placeholder"),
            name: "name",
            id: "name",
            type: "text",
            handleChange: handleChange,
            children: (
                <input
                    type="text"
                    name="name"
                    placeholder={t("fighterRegister.fields.name.placeholder")}
                    className="input input-ghost border-0 border-b-2 px-1 border-base-300 focus:outline-none focus:border-current w-full"
                    value={formData.name}
                    onChange={handleChange}
                    required
                />
            ),
        };

        const nationalityInput = {
            title: t("fighterRegister.fields.nationality.title"),
            desc: t("fighterRegister.fields.nationality.desc"),
            placeholder: t("fighterRegister.fields.nationality.placeholder"),
            name: "nationality",
            id: "nationality",
            type: "select",
            handleChange: handleChange,
            children: (
                <select
                    name="nationality"
                    className="select select-ghost border-0 border-b-2 px-1 border-base-300 focus:outline-none focus:border-current w-full py-2"
                    value={formData.nationality || "Malaysian"}
                    onChange={handleChange}
                >
                    {nationalities.map((nationality) => (
                        <option key={nationality} value={nationality}>
                            {nationality}
                        </option>
                    ))}
                </select>
            ),
        };

        const emailInput = {
            title: t("fighterRegister.fields.email.title"),
            desc: t("fighterRegister.fields.email.desc"),
            placeholder: t("fighterRegister.fields.email.placeholder"),
            name: "email",
            id: "email",
            type: "email",
            handleChange: handleChange,
            children: (
                <input
                    type="email"
                    name="email"
                    placeholder={t("fighterRegister.fields.email.placeholder")}
                    className="input input-ghost border-0 border-b-2 px-1 border-base-300 w-full focus:outline-none focus:border-current"
                    value={formData.email}
                    onChange={handleChange}
                />
            ),
        };

        const birthdateInput = {
            title: t("fighterRegister.fields.birthdate.title"),
            desc: "",
            placeholder: "",
            name: "birthdate",
            id: "birthdate",
            type: "date",
            handleChange: handleChange,
            children: (
                <input
                    type="date"
                    name="birthdate"
                    id="birthdate"
                    className="input input-ghost border-0 border-b-2 px-1 border-base-300 focus:outline-none focus:border-current w-full"
                    value={birthdateString}
                    onChange={handleChange}
                    max={new Date().toISOString().split('T')[0]}
                    min="1900-01-01"
                    required
                />
            ),
        };

        const ageInput = {
            title: t("fighterRegister.fields.age.title"),
            desc: t("fighterRegister.fields.age.desc"),
            placeholder: t("fighterRegister.fields.age.placeholder"),
            name: "age",
            id: "age",
            type: "number",
            handleChange: handleChange,
            children: (
                <input
                    type="number"
                    name="age"
                    placeholder={t("fighterRegister.fields.age.placeholder")}
                    className="input input-ghost border-0 border-b-2 pl-1 border-base-300 focus:outline-none focus:border-current w-full"
                    value={formData.age}
                    onChange={handleChange}
                />
            ),
        };

        const genderInput = {
            title: t("fighterRegister.fields.gender.title"),
            desc: (
                <div className="flex flex-row gap-2 items-center">
                    <Mars size={18} className="mt-1" /> <Venus size={18} />
                </div>
            ),
            placeholder: t("fighterRegister.fields.gender.placeholder"),
            name: "gender",
            id: "gender",
            type: "select",
            handleChange: handleChange,
            children: (
                <select
                    name="gender"
                    className="select select-ghost border-0 border-b-2 px-1 border-base-300 focus:outline-none focus:border-current w-full py-2"
                    value={formData.gender}
                    onChange={handleChange}
                >
                    <option defaultChecked disabled value="">
                        {t("fighterRegister.fields.gender.placeholder")}
                    </option>
                    <option value="M">
                        {t("fighterRegister.fields.gender.options.male")}
                    </option>
                    <option value="F">
                        {t("fighterRegister.fields.gender.options.female")}
                    </option>
                </select>
            ),
        };

        const phoneNumberInput = {
            title: t("fighterRegister.fields.phoneNumber.title"),
            desc: t("fighterRegister.fields.phoneNumber.desc"),
            placeholder: t("fighterRegister.fields.phoneNumber.placeholder"),
            name: "phoneNumber",
            id: "phoneNumber",
            type: "text",
            handleChange: handleChange,
            children: (
                <input
                    type="text"
                    name="phoneNumber"
                    placeholder={t(
                        "fighterRegister.fields.phoneNumber.placeholder"
                    )}
                    className="input input-ghost border-0 border-b-2 px-1 border-base-300 focus:outline-none focus:border-current w-full"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    inputMode="tel"
                    pattern="[0-9+-\s]*"
                />
            ),
        };

        const emergencyContactNumberInput = {
            title: t("fighterRegister.fields.emergencyContact.title"),
            desc: t("fighterRegister.fields.emergencyContact.desc"),
            placeholder: t(
                "fighterRegister.fields.emergencyContact.placeholder"
            ),
            name: "emergencyContactNumber",
            id: "emergencyContactNumber",
            type: "text",
            handleChange: handleChange,
            children: (
                <input
                    type="text"
                    inputMode="tel"
                    name="emergencyContactNumber"
                    placeholder={t(
                        "fighterRegister.fields.emergencyContact.placeholder"
                    )}
                    className="input input-ghost border-0 border-b-2 px-1 border-base-300 focus:outline-none focus:border-current w-full"
                    value={formData.emergencyContactNumber}
                    onChange={handleChange}
                />
            ),
        };

        const icOrPassportInput = {
            title: t("fighterRegister.fields.idPassport.title"),
            desc: t("fighterRegister.fields.idPassport.desc"),
            placeholder: t("fighterRegister.fields.idPassport.placeholder"),
            name: "icOrPassport",
            id: "icOrPassport",
            type: "text",
            handleChange: handleChange,
            children: (
                <input
                    type="text"
                    inputMode="numeric"
                    name="icOrPassport"
                    placeholder={t(
                        "fighterRegister.fields.idPassport.placeholder"
                    )}
                    className="input input-ghost border-0 border-b-2 px-1 border-base-300 focus:outline-none focus:border-current w-full"
                    value={formData.icOrPassport}
                    onChange={handleChange}
                />
            ),
        };

        const gymNameOrTeamNameInput = {
            title: t("fighterRegister.fields.gymTeam.title"),
            desc: (
                <span>
                    {t("fighterRegister.fields.gymTeam.desc")}{" "}
                    <span className="badge badge-primary">
                        {t("fighterRegister.fields.gymTeam.independent")}
                    </span>{" "}
                    {t("fighterRegister.fields.gymTeam.or")}
                </span>
            ),
            placeholder: t("fighterRegister.fields.gymTeam.placeholder"),
            name: "gymNameOrTeamName",
            id: "gymNameOrTeamName",
            type: "text",
            handleChange: handleChange,
            children: (
                <input
                    type="text"
                    name="gymNameOrTeamName"
                    placeholder={t(
                        "fighterRegister.fields.gymTeam.placeholder"
                    )}
                    className="input input-ghost border-0 border-b-2 px-1 border-base-300 focus:outline-none focus:border-current w-full"
                    value={formData.gymNameOrTeamName}
                    onChange={handleChange}
                />
            ),
        };

        const weightClassInput = {
            title: t("fighterRegister.fields.weightClass.title"),
            desc: t("fighterRegister.fields.weightClass.desc"),
            placeholder: t("fighterRegister.fields.weightClass.placeholder"),
            name: "weightClass",
            id: "weightClass",
            type: "select",
            handleChange: handleChange,
            children: (
                <select
                    className="w-full select select-ghost border-0 border-b-2 px-1 border-base-300 focus:outline-none focus:border-current py-2"
                    name="weightClass"
                    id="weightClass"
                    value={formData?.weightClass ?? ""}
                    onChange={handleChange}
                >
                    <option value={""} defaultChecked disabled>
                        Please select a Weight Class
                    </option>
                    <option value="A">{"A: Atomweight (48 kg)"}</option>
                    <option value="B">
                        {"B: Strawweight 115 lb (52.2 kg)"}
                    </option>
                    <option value="C">{"C: Flyweight 125 lb (56.7 kg)"}</option>
                    <option value="D">
                        {"D: Bantamweight 135 lb (61.2 kg)"}
                    </option>
                    <option value="E">
                        {"E: Featherweight 145 lb (65.8 kg)"}
                    </option>
                    <option value="F">
                        {"F: Lightweight 155 lb (70.3 kg)"}
                    </option>
                    <option value="G">
                        {"G: Welterweight 170 lb (77.1 kg)"}
                    </option>
                    <option value="H">
                        {"H: Middleweight 185 lb (83.9 kg)"}
                    </option>
                    <option value="I">
                        {"I: Light Heavyweight 205 lb (93.0 kg)"}
                    </option>
                    <option value="J">
                        {"J: Heavyweight 265 lb (120.2 kg)"}
                    </option>
                    <option value="K">
                        {"K: Super Heavyweight (> 120 kg)"}
                    </option>
                </select>
            ),
        };

        const heightInput = {
            title: t("fighterRegister.fields.height.title"),
            desc: t("fighterRegister.fields.height.desc"),
            placeholder: t("fighterRegister.fields.height.placeholder"),
            name: "height",
            id: "height",
            type: "number",
            handleChange: handleChange,
            children: (
                <input
                    type="number"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    name="height"
                    placeholder={t("fighterRegister.fields.height.placeholder")}
                    className="input input-ghost border-0 border-b-2 pl-1 border-base-300 focus:outline-none focus:border-current w-full"
                    value={formData.height}
                    onChange={handleChange}
                />
            ),
        };

        const recordsInput = {
            title: t("fighterRegister.fields.records.title"),
            desc: "",
            placeholder: "",
            name: "records",
            id: "records",
            type: "custom",
            handleChange: null,
            children: (
                <FighterRecordsTable
                    records={formData.records}
                    handleRecordChange={handleRecordChange}
                ></FighterRecordsTable>
            ),
        };

        const walkOutMusicInput = {
            title: t("fighterRegister.fields.walkoutMusic.title"),
            desc: t("fighterRegister.fields.walkoutMusic.desc"),
            placeholder: t("fighterRegister.fields.walkoutMusic.placeholder"),
            name: "walkOutMusic",
            id: "walkOutMusic",
            type: "text",
            handleChange: handleChange,
            children: (
                <input
                    type="text"
                    name="walkOutMusic"
                    placeholder={t(
                        "fighterRegister.fields.walkoutMusic.placeholder"
                    )}
                    className="input input-ghost border-0 border-b-2 px-1 border-base-300 focus:outline-none focus:border-current w-full"
                    value={formData.walkOutMusic}
                    onChange={handleChange}
                />
            ),
        };

        const igUsernameInput = {
            title: t("fighterRegister.fields.instagram.title"),
            desc: t("fighterRegister.fields.instagram.desc"),
            placeholder: t("fighterRegister.fields.instagram.placeholder"),
            name: "igUsername",
            id: "igUsername",
            type: "text",
            handleChange: handleChange,
            children: (
                <input
                    type="text"
                    name="igUsername"
                    placeholder={t(
                        "fighterRegister.fields.instagram.placeholder"
                    )}
                    className="input input-ghost border-0 border-b-2 px-1 border-base-300 focus:outline-none focus:border-current w-full"
                    value={formData.igUsername}
                    onChange={handleChange}
                />
            ),
        };

        const fighterPictureInput = {
            title: t("fighterRegister.fields.fighterPicture.title"),
            desc: (
                <div className="flex-center">
                    <p className="mb-2">
                        {t("fighterRegister.fields.fighterPicture.desc")}
                    </p>
                    <p className="text-start w-full">
                        {t("fighterRegister.fields.fighterPicture.preview")}
                    </p>
                    <div className=" border p-4 w-full overflow-hidden">
                        {!formData?.fighterPicture ? (
                            <img
                                className="aspect-square object-cover object-center opacity-50"
                                src="/assets/img/example_fighter_picture.png"
                                alt=""
                            />
                        ) : (
                            <Preview src={formData.fighterPicture} />
                        )}
                    </div>
                </div>
            ),
            placeholder: "",
            name: "fighterPicture",
            id: "fighterPicture",
            type: "file",
            handleChange: handleFileChange,
            children: (
                <input
                    ref={fighterPictureInputButton}
                    type="file"
                    name="fighterPicture"
                    className="file-input file-input-bordered w-full border-current"
                    accept="image/*"
                    onChange={handleFileChange}
                />
            ),
        };

        const submit = {
            id: "submit",
            title: t("fighterRegister.submit.title"),
            desc: (
                <span>
                    <p>{t("fighterRegister.submit.desc1")}</p>
                    <p>{t("fighterRegister.submit.desc2")}</p>
                </span>
            ),
            type: "custom",
            containerClassName: "max-w-none justify-start overflow-y-auto",
            children: (
                <>
                    <div className="divier" />
                    <div className="flex-center items-start justify-start h-full w-full">
                        <div className="flex flex-col w-full h-full gap-12">
                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">
                                    Profile Picture
                                </p>
                                <div className="flex-center w-full">
                                    <div className="flex flex-row w-full gap-2 justify-stretch">
                                        <div className="flex-center w-full">
                                            {!formData?.fighterPicture ? null : (
                                                <Preview
                                                    className={"w-52"}
                                                    src={
                                                        formData.fighterPicture
                                                    }
                                                />
                                            )}
                                        </div>
                                        <button
                                            className="flex-center hover:bg-base-200 p-2"
                                            onClick={() =>
                                                fighterPictureInputButton?.current?.click()
                                            }
                                        >
                                            <Pen />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">Email</p>
                                <div className="w-full">
                                    {emailInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">
                                    Phone Number
                                </p>
                                <div className="w-full">
                                    {phoneNumberInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">
                                    Full Name
                                </p>
                                <div className="w-full">
                                    {nameInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">
                                    Gender
                                </p>
                                <div className="w-full">
                                    {genderInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">
                                    Date of Birth
                                </p>
                                <div className="w-full">
                                    {birthdateInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">Age</p>
                                <div className="w-full">
                                    {ageInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">
                                    Height (cm)
                                </p>
                                <div className="w-full">
                                    {heightInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">
                                    Weight Class
                                </p>
                                <div className="w-full">
                                    {weightClassInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">
                                    Nationality
                                </p>
                                <div className="w-full">
                                    {nationalityInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">
                                    ID/Passport Number
                                </p>
                                <div className="w-full">
                                    {icOrPassportInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">
                                    Emergency Contact
                                </p>
                                <div className="w-full">
                                    {emergencyContactNumberInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">
                                    Instagram
                                </p>
                                <div className="w-full">
                                    {igUsernameInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                                <p className="font-bold min-w-[200px]">
                                    Gym/Team
                                </p>
                                <div className="w-full">
                                    {gymNameOrTeamNameInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center pt-4">
                                <p className="font-bold min-w-[200px]">
                                    Walkout Music
                                </p>
                                <div className="w-full">
                                    {walkOutMusicInput.children}
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-start md:items-center border-t-2 pt-4">
                                <p className="font-bold min-w-[200px]">
                                    Records (W-L-D)
                                </p>
                                <div className="w-full">
                                    {recordsInput.children}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="h-4"></div>
                </>
            ),
            button: (
                <button
                    className="btn btn-primary w-full"
                    onClick={handleSubmit}
                >
                    {t("fighterRegister.submit.button")}
                </button>
            ),
        };
        if (!!id || !!formData.id) {
            return [
                acknowledgement,
                submit,
                fighterPictureInput,
                emailInput,
                nameInput,
                genderInput,
                birthdateInput,
                ageInput,
                heightInput,
                weightClassInput,
                nationalityInput,
                icOrPassportInput,
                phoneNumberInput,
                emergencyContactNumberInput,
                igUsernameInput,
                gymNameOrTeamNameInput,
                walkOutMusicInput,
                recordsInput,
            ];
        }
        return [
            acknowledgement,
            fighterPictureInput,
            emailInput,
            phoneNumberInput,
            nameInput,
            genderInput,
            birthdateInput,
            ageInput,
            heightInput,
            weightClassInput,
            nationalityInput,
            icOrPassportInput,
            emergencyContactNumberInput,
            igUsernameInput,
            gymNameOrTeamNameInput,
            walkOutMusicInput,
            recordsInput,
            submit,
        ];
    }, [
        formData.name,
        formData?.nationality,
        formData.email,
        formData?.birthdate,
        formData.age,
        formData.gender,
        formData.phoneNumber,
        formData.emergencyContactNumber,
        formData.icOrPassport,
        formData.gymNameOrTeamName,
        formData?.weightClass,
        formData.height,
        formData.walkOutMusic,
        formData.igUsername,
        formData.fighterPicture,
        formData.id,
        formData.records,
        handleSubmit,
        id,
        t,
        birthdateString,
    ]);

    const prev = useCallback(() => {
        if (animating) return;
        if (!(currentIndex === 0)) {
            setCurrentIndex((prev) => Math.max(0, prev - 1));
        }
    }, [animating, currentIndex, inputs]);

    const verifyCurrentInput = useCallback(() => {
        const key = inputs[currentIndex].name ?? inputs[currentIndex].id;
        const value = formData[key];
        if (!key) {
            return true;
        }

        switch (key) {
            case "name":
                if (typeof value !== "string" || value.trim().length === 0) {
                    toast.error(t("fighterRegister.errors.name"));
                    return false;
                }
                return true;

            case "nationality":
                if (typeof value !== "string" || value.trim().length === 0) {
                    toast.error(t("fighterRegister.errors.nationality"));
                    return false;
                }
                return true;

            case "email":
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (typeof value !== "string" || !emailRegex.test(value)) {
                    toast.error(t("fighterRegister.errors.email"));
                    return false;
                }
                return true;

            case "birthdate":
                if (!value?.toDate()) {
                    toast.error(t("fighterRegister.errors.birthdate"));
                    return false;
                }
                return true;

            case "age":
            case "height":
                if (isNaN(value) || value <= 0) {
                    toast.error(t(`fighterRegister.errors.empty`));
                    return false;
                }
                if (key === "age" && (value < 12 || value > 120)) {
                    toast.error(t("fighterRegister.errors.ageRange"));
                    return false;
                }
                return true;

            case "gender":
            case "entry":
            case "weightClass":
                if (typeof value !== "string" || value.trim().length === 0) {
                    toast.error(t(`fighterRegister.errors.${key}`));
                    return false;
                }
                return true;

            case "phoneNumber":
            case "emergencyContactNumber":
                if (
                    typeof value !== "string" ||
                    value.replace(/\D/g, "").length < 8
                ) {
                    toast.error(t(`fighterRegister.errors.${key}`));
                    return false;
                }
                return true;

            case "icOrPassport":
                if (typeof value !== "string" || value.trim().length < 6) {
                    toast.error(t("fighterRegister.errors.icOrPassport"));
                    return false;
                }
                return true;

            case "gymNameOrTeamName":
                if (typeof value !== "string" || value.trim().length === 0) {
                    toast.error(t("fighterRegister.errors.gymNameOrTeamName"));
                    return false;
                }
                return true;

            case "fighterRecord":
                const recordRegex = /^\d+-\d+-\d+$/;
                if (typeof value !== "string" || !recordRegex.test(value)) {
                    toast.error(t("fighterRegister.errors.fighterRecord"));
                    return false;
                }
                return true;

            case "walkOutMusic":
                if (typeof value !== "string" || value.trim().length === 0) {
                    toast.error(t("fighterRegister.errors.walkOutMusic"));
                    return false;
                }
                return true;

            case "igUsername":
                if (
                    typeof value !== "string" ||
                    !value.startsWith("@") ||
                    value.length < 2
                ) {
                    toast.error(t("fighterRegister.errors.igUsername"));
                    return false;
                }
                return true;

            case "fighterPicture":
                if (!value) {
                    toast.error(t("fighterRegister.errors.fighterPicture"));
                    return false;
                }
                return true;

            default:
                if (value === undefined || value === null || value === "") {
                    toast.error(t("fighterRegister.errors.empty"));
                    return false;
                }
                return true;
        }
    }, [currentIndex, formData, inputs, t]);

    const next = useCallback(() => {
        if (animating) return;
        if (inputs[currentIndex].id === "submit") return;
        if (inputs[currentIndex].id === "birthdate") {
            if (!birthdateString) {
                return toast.error(t("fighterRegister.errors.birthdate"));
            }

            const date = new Date(birthdateString);
            if (isNaN(date.getTime())) {
                return toast.error(t("fighterRegister.errors.birthdate"));
            }

            // Validate date is not in the future
            if (date > new Date()) {
                return toast.error(t("fighterRegister.errors.birthdate"));
            }

            const a = calculateAge(date);
            setFormData((prev) => {
                return {
                    ...prev,
                    birthdate: Timestamp.fromDate(date),
                    age: a,
                };
            });
        }
        if (!(currentIndex === inputs.length - 1)) {
            if (verifyCurrentInput()) {
                setCurrentIndex((prev) =>
                    Math.min(
                        prev + 1,
                        !!id || !!formData?.id ? 1 : inputs.length - 1
                    )
                );
            }
        }
    }, [
        animating,
        currentIndex,
        formData?.id,
        id,
        inputs.length,
        verifyCurrentInput,
        birthdateString,
    ]);

    useGSAP(
        () => {
            setAnimating(true);
            if (inputsRef.current[prevIndex]) {
                gsap.fromTo(
                    inputsRef.current[prevIndex],
                    {
                        opacity: 1,
                        display: "flex",
                        y: "0%",
                        position: "absolute",
                    },
                    {
                        opacity: 0,
                        display: "none",
                        y: prevIndex < currentIndex ? "-120%" : "120%",
                        ease: "expo.inOut",
                        position: "absolute",
                        // duration: 1,
                    }
                );
            }
            gsap.fromTo(
                inputsRef.current[currentIndex],
                {
                    opacity: 0,
                    display: "flex",
                    y: prevIndex <= currentIndex ? "120%" : "-120%",
                },
                {
                    position: "relative",
                    opacity: 1,
                    delay: 0.1,
                    display: "flex",
                    y: "0%",
                    ease: "expo.inOut",
                    onComplete: () => {
                        setAnimating(false);
                        const inputElement =
                            inputsRef?.current?.[currentIndex]?.querySelector(
                                "input"
                            );
                        const selectElement =
                            inputsRef?.current?.[currentIndex]?.querySelector(
                                "select"
                            );

                        if (inputElement) {
                            inputElement.focus();
                        } else if (selectElement) {
                            selectElement.focus();
                        }
                        window.setTimeout(() => { }, 10);
                    },
                    // duration: 1,
                }
            );

            const clearItems = inputsRef.current.filter(
                (r, index) => index !== currentIndex || index !== prevIndex
            );
            gsap.set(clearItems, {
                opacity: 0,
                display: "none",
            });

            setPrevIndex(currentIndex);
        },
        {
            dependencies: [currentIndex],
            scope: fighterRegisterContainer?.current,
        }
    );

    // useContainerWheelNavigation(fighterRegisterContainer, next, prev);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Enter") {
                event.preventDefault();
                next();
            }
        };
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [next]);

    useEffect(() => {
        (async () => {
            if (id) {
                const registerRequestSnapsnot = await getDoc(
                    doc(fighterRequestsCollection, id)
                );
                if (registerRequestSnapsnot.exists()) {
                    const requestData = registerRequestSnapsnot.data();

                    // Verify that the request belongs to the current user
                    if (requestData.uid !== currentUser?.uid) {
                        toast.error(
                            "You don't have permission to edit this request"
                        );
                        navigate(PROFILE_PATH);
                        return;
                    }

                    const data = requestData.data;
                    const d = data.birthdate
                        ? Timestamp.fromMillis(
                            (data.birthdate?._seconds ||
                                data.birthdate?.seconds) * 1000
                        ).toDate()
                        : null;
                    if (d) {
                        // Convert Date to YYYY-MM-DD format for native date input
                        const year = d.getFullYear();
                        const month = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        setBirthdateString(`${year}-${month}-${day}`);
                    }

                    setFormData((prev) => {
                        return {
                            ...prev,
                            id: id,
                            ...data,
                            nationality: data.nationality || "Malaysian",
                        };
                    });
                } else {
                    navigate("/404");
                }
            } else {
                const q = query(
                    fighterRequestsCollection,
                    where("uid", "==", currentUser?.uid),
                    limit(1),
                    orderBy("updateAt", "desc")
                );
                const snapshot = await getDocs(q);
                if (!snapshot.empty) {
                    const data = snapshot.docs[0].data().data;

                    const d = data.birthdate
                        ? Timestamp.fromMillis(
                            (data.birthdate?._seconds ||
                                data.birthdate?.seconds) * 1000
                        ).toDate()
                        : null;

                    if (d) {
                        // Convert Date to YYYY-MM-DD format for native date input
                        const year = d.getFullYear();
                        const month = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        setBirthdateString(`${year}-${month}-${day}`);
                    }

                    setFormData((prev) => {
                        const loadedData = snapshot.docs[0].data().data;
                        return {
                            ...prev,
                            id: snapshot.docs[0].id,
                            ...loadedData,
                            nationality: loadedData.nationality || "Malaysian",
                        };
                    });
                }
            }
        })();
    }, [currentUser?.uid, currentUser, id, navigate]);

    return (
        <>
            <div
                ref={fighterRegisterContainer}
                className="w-full h-[calc(100vh-6.5rem)] flex-center p-4 max-[500px]:p-2 overflow-hidden relative"
                onWheel={handleWheel}
            >
                <button
                    className="absolute top-4 right-4 btn-sm btn btn-ghost btn-square"
                    onClick={handleClose}
                >
                    <X />
                </button>
                <div className="flex-center w-full h-full overflow-scroll">
                    {inputs.map((input, index) => {
                        return (
                            <div
                                key={index}
                                ref={(el) => (inputsRef.current[index] = el)}
                                className={twMerge(
                                    "h-full justify-center items-center flex-col w-full max-w-[30rem] hidden overflow-hidden",
                                    input.containerClassName ?? ""
                                )}
                            >
                                <div className="flex justify-start items-start gap-2 !text-xl w-full overflow-y-auto">
                                    <p className="hidden md:flex gap-2 flex-row justify-start items-end leading-none text-sm pt-2">
                                        {index + 1} <MoveRight size={12} />
                                    </p>
                                    <div className="flex flex-col items-start gap-2 w-full">
                                        <p className="text-xl">
                                            <span className="inline md:hidden">
                                                {index + 1}.
                                            </span>{" "}
                                            {input?.title}
                                        </p>
                                        <div className="text-sm text-gray-500">
                                            {input?.desc}
                                        </div>
                                        {input.children}
                                        {input?.button ?? (
                                            <>
                                                <div className="flex flex-row gap-2 items-center text-xs mt-2 w-full">
                                                    <button
                                                        onClick={next}
                                                        className="btn btn-primary btn-sm"
                                                    >
                                                        {input?.buttonText ??
                                                            "OK"}
                                                    </button>
                                                    <p className="leading-none">
                                                        press <b>Enter</b> ↵
                                                    </p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="flex flex-row justify-center items-center w-[calc(100vw-2rem)] bottom-2 left-4 p-2">
                    <LanguageSwitcher />
                    <div className="flex-1" />
                    <p className="font-bold opacity-70">
                        {String(currentIndex + 1).padStart(2, "0")} /{" "}
                        {!!id || !!formData?.id
                            ? "02"
                            : String(inputs.length).padStart(2, "0")}
                    </p>
                    <div className="w-3" />
                    <button
                        ref={prevButton}
                        className="btn btn-square btn-outline btn-sm"
                        disabled={animating || currentIndex === 0}
                        onClick={prev}
                    >
                        <ChevronUp />
                    </button>
                    <button
                        ref={nextButton}
                        className="btn btn-square btn-outline btn-sm"
                        disabled={
                            animating ||
                            currentIndex === inputs.length - 1 ||
                            (!!id && currentIndex === 1)
                        }
                        onClick={next}
                    >
                        <ChevronDown />
                    </button>
                </div>
            </div>
        </>
    );
}
